import { stringifyListSearch } from './search-serialization.js';
import { PublicPortal } from './public-portal.js';
import { CompanyPaymentReportsScreen } from './company-payment-reports-screen.js';
import { CompanyPrepaidWithdrawalsScreen } from './company-prepaid-withdrawals-screen.js';
import { CompanyPrepaidSettingsScreen } from './company-prepaid-settings-screen.js';
import { CompanyOnlinePaymentsSettingsScreen } from './company-online-payments-settings-screen.js';
import { CompanyCardSettingsScreen } from './company-card-settings-screen.js';
import { CompanyCommissionRulesScreen } from './company-commission-rules-screen.js';
import { CompanyCommissionsScreen } from './company-commissions-screen.js';
import { CompanySalesSettingsScreen } from './company-sales-settings-screen.js';
import { CompanySchedulingSettingsScreen } from './company-scheduling-settings-screen.js';
import { CompanyCustomerPortalSettingsScreen } from './company-customer-portal-settings-screen.js';
import {
  AccountSignupScreen,
  AccountConfirmationScreen,
  EmailConfirmationScreen,
} from './account-signup.js';
import { CustomerAccountScreen, type CustomerAccountTab } from './customer-account.js';
import { LegalDocumentScreen } from './legal-documents.js';
import { BackToTop, SnackbarProvider } from '@hue/design-system';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useCanGoBack,
  useNavigate,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { z } from 'zod';
import { contextApi, rootApi, type SessionUser } from '../lib/api.js';
import { loadSession, refreshSession, SessionNotEstablishedError } from '../lib/session.js';
import { RootPlans } from '../modules/billing/index.js';
import { AppErrorBoundary } from './app-error-boundary.js';
import { ActivationScreen, PasswordRecoveryScreen, PendingSession } from './auth-screens.js';
import { CompanyAppearanceScreen } from './company-appearance.js';
import { CompanySecurityScreen } from './company-security.js';
import { CompanySettingsLayout } from './company-settings.js';
import { useCompanyScope } from './company-context.js';
import { CompanyHome } from './company-home.js';
import { CompanyLayout } from './company-layout.js';
import { CompanyBusinessUnits } from './company-business-units.js';
import { CompanyProfileScreen } from './company-profile.js';
import {
  CompanyAccessScreen,
  CompanyAuditScreen,
  CompanyContributionsScreen,
  CompanyEntriesScreen,
  CompanyMetricsScreen,
  CompanyOutboxScreen,
  CompanyPartnersScreen,
  CompanyTemplatesScreen,
  CompanyWhatsAppScreen,
} from './company-screens.js';
import { AppointmentClosingScreen } from './appointment-closing.js';
import { ClientPageScreen } from './client-page.js';
import { CompanyClientsScreen } from './company-clients-screen.js';
import { CompanyMaterialUsageScreen } from './company-material-usage-screen.js';
import { CompanyMaterialSufficiencyScreen } from './company-material-sufficiency-screen.js';
import { CompanyProtocolApplicationsScreen } from './company-protocol-applications-screen.js';
import { CompanyProtocolsScreen } from './company-protocols-screen.js';
import { CompanyRecordTemplatesScreen } from './company-record-templates-screen.js';
import { CompanyHealthThemesScreen } from './company-health-themes-screen.js';
import { ClientRecordsButton } from '../modules/records/index.js';
import { CompanyServicesScreen } from './company-services-screen.js';
import {
  CompanyPackagesScreen,
  CompanyPromotionsScreen,
  CompanySalesPackageScreen,
  CompanySalesScreen,
} from './company-sales-screen.js';
import { CompanyInventoryScreen, CompanyProductsScreen } from './company-inventory-screen.js';
import { CompanyPurchasingScreen } from './company-purchasing-screen.js';
import { CompanySchedulingScreen } from './company-scheduling-screen.js';
import { CompanyProfessionalsScreen } from './company-professionals-screen.js';
import { CompanyFinanceScreen } from './company-finance-screen.js';
import { CompanyPayablesScreen } from './company-payables-screen.js';
import { CompanyChartOfAccountsScreen } from './company-chart-of-accounts-screen.js';
import {
  CompanyAccountingClosingScreen,
  CompanyCashFlowScreen,
  CompanyDreScreen,
} from './company-accounting-reports-screen.js';
import { CompanyClassificationRulesScreen } from './company-classification-rules-screen.js';
import { CompanyClassificationScreen } from './company-classification-screen.js';
import { ConsoleCompanies } from './console-companies.js';
import { ConsoleCommunication, ConsoleOutbox, ConsoleTemplates } from './console-communication.js';
import { ConsoleLayout } from './console-layout.js';
import { ConsolePlatformAccess } from './console-platform-access.js';
import {
  accessViewDefaults,
  clientPageViewDefaults,
  clientPageViewSchema,
  clientsViewDefaults,
  clientsViewSchema,
  materialUsageViewDefaults,
  materialUsageViewSchema,
  materialSufficiencyViewDefaults,
  materialSufficiencyViewSchema,
  protocolApplicationsViewDefaults,
  protocolApplicationsViewSchema,
  protocolsViewDefaults,
  protocolsViewSchema,
  recordTemplatesViewDefaults,
  recordTemplatesViewSchema,
  servicesViewDefaults,
  servicesViewSchema,
  salesViewDefaults,
  newSaleViewDefaults,
  salesViewSchema,
  salesTermsViewDefaults,
  salesTermsViewSchema,
  productsViewDefaults,
  productsViewSchema,
  inventoryViewDefaults,
  inventoryViewSchema,
  purchasingViewDefaults,
  purchasingViewSchema,
  professionalsViewDefaults,
  professionalsViewSchema,
  schedulingViewDefaults,
  schedulingViewSchema,
  financeViewDefaults,
  financeViewSchema,
  payablesViewDefaults,
  classificationRulesViewDefaults,
  classificationRulesViewSchema,
  cashFlowViewDefaults,
  cashFlowViewSchema,
  dreViewDefaults,
  dreViewSchema,
  classificationViewDefaults,
  classificationViewSchema,
  payablesViewSchema,
  plansViewDefaults,
  plansViewSchema,
  companiesViewDefaults,
  companiesViewSchema,
  platformAccessViewDefaults,
  platformAccessViewSchema,
  accessViewSchema,
  auditViewDefaults,
  auditViewSchema,
  contributionsViewDefaults,
  contributionsViewSchema,
  entriesViewDefaults,
  entriesViewSchema,
  outboxViewDefaults,
  outboxViewSchema,
  partnersViewDefaults,
  partnersViewSchema,
  templatesViewDefaults,
  templatesViewSchema,
} from './list-views.js';
import { CommunicationSettings } from './communication-settings.js';
import { RootLogin, type LoginMode } from './root-login.js';
import { RouteNotFound } from './route-not-found.js';
import { useViewState } from './view-state.js';
import { SystemDocumentation } from './system-documentation.js';
import { CompanyDocumentation } from './page-help.js';

/// O `mode` diz de qual porta a pessoa veio: a da empresa ou a da plataforma. Ele viaja na URL
/// porque a recuperação de senha precisa saber para onde voltar, e "voltar para entrar" caindo na
/// porta errada é o tipo de detalhe que só aparece com alguém preso do lado de fora.
const loginModeSearch = z.object({
  mode: z.enum(['user', 'root']).optional().catch(undefined),
});

/// O token vem do link do e-mail. Ausente ou lixo, o endereço não tem o que fazer: quem chega sem
/// token não está ativando nada, está só num endereço solto.
const tokenSearch = z.object({
  token: z.string().optional().catch(undefined),
});

const resetPasswordSearch = loginModeSearch.extend(tokenSearch.shape);

/// Para onde voltar depois de entrar ou de criar a conta: só caminho interno, nunca outro site.
const nextSearch = z.object({
  next: z
    .string()
    .regex(/^\/(?!\/)[\w\-./?=&%]*$/u)
    .optional()
    .catch(undefined),
});

interface RouterContext {
  queryClient: QueryClient;
}

/// Tudo que precisa estar acima de qualquer tela mora aqui: o limite de erro, o lugar de onde o
/// resultado de uma ação aparece, e o botão de voltar ao topo. Antes isso ficava no `App`; com
/// rota, precisa ficar no topo da árvore de rotas, senão a tela de endereço inexistente nasceria
/// sem nada disso.
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: function RouteShell() {
    const pathname = useRouterState({ select: (state) => state.location.pathname });
    return (
      <AppErrorBoundary>
        <SnackbarProvider scope={pathname}>
          <Outlet />
          <BackToTop />
        </SnackbarProvider>
      </AppErrorBoundary>
    );
  },
});

/// A entrada. Quem já entrou não tem o que fazer aqui — e mandar de volta para o início evita a
/// cena de digitar a senha de novo para descobrir que já estava dentro.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: loginModeSearch.extend(nextSearch.shape),
  beforeLoad: async ({ context, search }) => {
    if (await loadSession(context.queryClient)) throw redirect({ to: search.next ?? '/home' });
  },
  component: function LoginScreen() {
    const { mode, next } = loginRoute.useSearch();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    return (
      <RootLogin
        initialMode={mode ?? 'user'}
        onForgotPassword={(nextMode: LoginMode) => {
          void navigate({
            to: '/forgot-password',
            search: { mode: nextMode === 'root' ? 'root' : undefined },
          });
        }}
        onSignup={() => void navigate({ to: '/signup', search: { next } })}
        onAuthenticated={async (chosen) => {
          // A entrada pode dar certo no servidor e o navegador não guardar o cookie (interface e
          // API em sites diferentes). Seguir assim mandaria a pessoa de volta para cá sem motivo.
          if (!(await refreshSession(queryClient))) throw new SessionNotEstablishedError();
          // Quem veio da página de uma empresa volta para lá, com a escolha guardada; quem entrou
          // como cliente sem origem vai para a própria área.
          await navigate({ to: next ?? (chosen === 'CUSTOMER' ? '/account' : '/home') });
        }}
      />
    );
  },
});

/// Pedir o link e usar o link são dois endereços porque são dois momentos: o segundo chega pronto
/// do e-mail, dias depois, sem o primeiro ter acontecido nesta máquina.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  validateSearch: loginModeSearch,
  component: function ForgotPasswordScreen() {
    const { mode } = forgotPasswordRoute.useSearch();
    return <PasswordRecoveryScreen token={null} mode={mode} />;
  },
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: resetPasswordSearch,
  component: function ResetPasswordScreen() {
    const { mode, token } = resetPasswordRoute.useSearch();
    return <PasswordRecoveryScreen token={token ?? null} mode={mode} />;
  },
});

/// O portão devolve o token já provado, e a tela recebe `string` em vez de `string | undefined`.
/// Repetir a checagem lá dentro seria escrever um caminho que o portão torna impossível — e caminho
/// impossível não tem como ser exercitado por teste, só ocupa lugar.
const activateOwnerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activate-owner',
  validateSearch: tokenSearch,
  beforeLoad: ({ search }) => {
    if (!search.token) throw redirect({ to: '/login' });
    return { token: search.token };
  },
  component: function ActivateOwnerScreen() {
    const { token } = activateOwnerRoute.useRouteContext();
    return <ActivationScreen token={token} kind="owner" />;
  },
});

const activateMemberRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activate-member',
  validateSearch: tokenSearch,
  beforeLoad: ({ search }) => {
    if (!search.token) throw redirect({ to: '/login' });
    return { token: search.token };
  },
  component: function ActivateMemberScreen() {
    const { token } = activateMemberRoute.useRouteContext();
    return <ActivationScreen token={token} kind="member" />;
  },
});

const activateClientRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activate-client',
  validateSearch: tokenSearch,
  beforeLoad: ({ search }) => {
    if (!search.token) throw redirect({ to: '/login' });
    return { token: search.token };
  },
  component: function ActivateClientScreen() {
    const { token } = activateClientRoute.useRouteContext();
    return <ActivationScreen token={token} kind="client" />;
  },
});

/// A raiz deixou de ser tela e virou porteira: agora todo lugar tem endereço próprio, e `/` só
/// sabe para qual deles a pessoa pertence. Quem chega sem sessão vai para a entrada pelo portão da
/// rota, e não pela tela — assim a tela nem chega a montar, e nenhuma consulta de dentro dela sai
/// para tomar 403.
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context.queryClient);
    if (!session) throw redirect({ to: '/login' });
    if (
      session.context?.kind === 'PLATFORM' ||
      (!session.context && session.platformRole === 'ROOT' && !session.currentTenant)
    ) {
      const permissions = session.context?.kind === 'PLATFORM' ? session.context.permissions : [];
      if (session.platformRole === 'ROOT' || permissions.includes('platform.tenants.read'))
        throw redirect({ to: '/companies' });
      if (permissions.includes('platform.access.read')) throw redirect({ to: '/platform-access' });
      if (permissions.includes('platform.plans.read')) throw redirect({ to: '/plans' });
      if (permissions.includes('platform.technical_documentation.read'))
        throw redirect({ to: '/technical-documentation' });
      if (
        permissions.some((permission) =>
          [
            'platform.email_messages.read',
            'platform.email_templates.read',
            'platform.communication_settings.read',
          ].includes(permission),
        )
      )
        throw redirect({ to: '/communication' });
    }
    if (session.context?.kind === 'CUSTOMER') throw redirect({ to: '/account' });
    if (!session.currentTenant) throw redirect({ to: '/login' });
    throw redirect({
      to: '/companies/$companyKey',
      params: { companyKey: session.currentTenant.key },
    });
  },
});

/// A casca do console vive numa rota sem caminho: ela não é um endereço, é o que envolve os
/// endereços da plataforma. Assim trocar de seção troca só o miolo — o menu e o cabeçalho não
/// remontam, e o que estava rolado não pula.
///
/// O portão é o inverso do início: quem tem empresa em contexto não está no console, e insistir
/// num endereço da plataforma com uma empresa aberta veria a casca errada.
const consoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'console',
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context.queryClient);
    if (!session) throw redirect({ to: '/login' });
    if (
      session.context?.kind !== 'PLATFORM' &&
      !(session.platformRole === 'ROOT' && !session.currentTenant)
    )
      throw redirect({ to: '/home' });
  },
  component: ConsoleLayout,
});

const companiesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/companies',
  validateSearch: companiesViewSchema,
  beforeLoad: ({ context }) =>
    requirePlatformPermission(context.queryClient, 'platform.tenants.read'),
  component: function ConsoleCompaniesRouteScreen() {
    const [view, onView] = useViewState(companiesRoute.useSearch(), companiesViewDefaults);
    return <ConsoleCompanies view={view} onView={onView} />;
  },
});

const platformAccessRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/platform-access',
  validateSearch: platformAccessViewSchema,
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context.queryClient);
    if (!session) throw redirect({ to: '/login' });
    if (!platformCan(session, 'platform.access.read')) throw redirect({ to: '/home' });
    return { canManage: platformCan(session, 'platform.access.manage') };
  },
  component: function ConsolePlatformAccessRouteScreen() {
    const [view, onView] = useViewState(
      platformAccessRoute.useSearch(),
      platformAccessViewDefaults,
    );
    const { canManage } = platformAccessRoute.useRouteContext();
    return <ConsolePlatformAccess view={view} onView={onView} canManage={canManage} />;
  },
});

const plansRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/plans',
  validateSearch: plansViewSchema,
  beforeLoad: ({ context }) =>
    requirePlatformPermission(context.queryClient, 'platform.plans.read'),
  component: function RootPlansRouteScreen() {
    const [view, onView] = useViewState(plansRoute.useSearch(), plansViewDefaults);
    return <RootPlans view={view} onView={onView} />;
  },
});

const technicalDocumentationRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/technical-documentation',
  beforeLoad: ({ context }) =>
    requirePlatformPermission(context.queryClient, 'platform.technical_documentation.read'),
  component: function TechnicalDocumentationRouteScreen() {
    return <SystemDocumentation audience="technical" />;
  },
});

/// Comunicação é um endereço com três abas dentro, e não três itens de menu: são ângulos do mesmo
/// assunto. O endereço nu não é tela nenhuma — cai na primeira aba.
const communicationRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/communication',
  component: ConsoleCommunication,
});

const communicationIndexRoute = createRoute({
  getParentRoute: () => communicationRoute,
  path: '/',
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context.queryClient);
    if (!session) throw redirect({ to: '/login' });
    /// A caixa de saída tem recorte na URL, e por isso o destino precisa dizer com qual recorte
    /// chega. Vazio é o recém-aberto — que é o certo para quem só clicou em "Comunicação".
    if (platformCan(session, 'platform.email_messages.read'))
      throw redirect({ to: '/communication/outbox', search: {} });
    if (platformCan(session, 'platform.email_templates.read'))
      throw redirect({ to: '/communication/templates', search: {} });
    throw redirect({ to: '/communication/settings' });
  },
});

function platformCan(session: SessionUser, permission: string): boolean {
  return (
    session.platformRole === 'ROOT' ||
    (session.context?.kind === 'PLATFORM' && session.context.permissions.includes(permission))
  );
}

const outboxRoute = createRoute({
  getParentRoute: () => communicationRoute,
  path: 'outbox',
  validateSearch: outboxViewSchema,
  beforeLoad: ({ context }) =>
    requirePlatformPermission(context.queryClient, 'platform.email_messages.read'),
  component: function ConsoleOutboxRouteScreen() {
    const [view, onView] = useViewState(outboxRoute.useSearch(), outboxViewDefaults);
    return <ConsoleOutbox view={view} onView={onView} />;
  },
});

const templatesRoute = createRoute({
  getParentRoute: () => communicationRoute,
  path: 'templates',
  validateSearch: templatesViewSchema,
  beforeLoad: ({ context }) =>
    requirePlatformPermission(context.queryClient, 'platform.email_templates.read'),
  component: function ConsoleTemplatesRouteScreen() {
    const [view, onView] = useViewState(templatesRoute.useSearch(), templatesViewDefaults);
    return <ConsoleTemplates view={view} onView={onView} />;
  },
});

const communicationSettingsRoute = createRoute({
  getParentRoute: () => communicationRoute,
  path: 'settings',
  beforeLoad: ({ context }) =>
    requirePlatformPermission(context.queryClient, 'platform.communication_settings.read'),
  component: CommunicationSettings,
});

async function requirePlatformPermission(
  queryClient: QueryClient,
  permission: string,
): Promise<void> {
  const session = await loadSession(queryClient);
  if (!session) throw redirect({ to: '/login' });
  if (!platformCan(session, permission)) throw redirect({ to: '/home' });
}

/// A empresa na URL, pela `key`.
///
/// **A URL é um pedido, não uma permissão.** Abrir o endereço de uma empresa em que a pessoa não
/// está faz a aplicação pedir a troca ao servidor; quem decide é o vínculo dela, e a RLS é o que
/// barra de verdade. Root entra em atuação administrativa; membro troca para outra membership
/// ativa. Quando não há vínculo, volta para a empresa atual sem revelar se a outra existe.
async function resolveCompany(queryClient: QueryClient, companyKey: string): Promise<void> {
  const session = await loadSession(queryClient);
  if (!session) throw redirect({ to: '/login' });
  if (session.currentTenant?.key === companyKey) return;
  if (session.platformRole !== 'ROOT') {
    const contexts = await contextApi.list();
    const company = contexts.find(
      (candidate) => candidate.kind === 'TENANT_MEMBER' && candidate.key === companyKey,
    );
    if (company?.kind === 'TENANT_MEMBER') {
      await contextApi.switch({ kind: 'TENANT_MEMBER', tenantId: company.tenantId });
      await refreshSession(queryClient);
      return;
    }
    if (!session.currentTenant) throw redirect({ to: '/home' });
    throw redirect({
      to: '/companies/$companyKey',
      params: { companyKey: session.currentTenant.key },
    });
  }
  const matches = await rootApi.tenants({ search: companyKey, activity: 'ALL' });
  const company = matches.find((candidate) => candidate.key === companyKey);
  /// Chave que não é de empresa nenhuma volta para a lista: o root está no console, e a lista é o
  /// lugar de onde ele escolhe. Um 404 aqui seria a mesma informação com um passo a mais.
  if (!company) throw redirect({ to: '/companies' });
  await rootApi.enterTenant(company.id);
  await refreshSession(queryClient);
}

/// A casca da empresa, e as nove telas de dentro dela.
const companyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/companies/$companyKey',
  beforeLoad: ({ context, params }) => resolveCompany(context.queryClient, params.companyKey),
  component: CompanyLayout,
});

const companyHomeRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: '/',
  component: CompanyHome,
});

const companyDocumentationRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'documentation',
  /// A página de onde a pessoa veio pelo ícone de ajuda: a Ajuda abre no fluxo dela.
  validateSearch: z.object({ from: z.string().max(40).optional().catch(undefined) }),
  component: CompanyDocumentation,
});

/// Configurações (ADR 0041): a engrenagem abre esta casca, com menu próprio ao lado. Não é
/// módulo nem portão — cada página continua com o portão do módulo dono.
const companySettingsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'settings',
  component: CompanySettingsLayout,
});

/// O endereço nu cai em Aparência, a única página que toda pessoa alcança.
const companySettingsIndexRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/companies/$companyKey/settings/appearance',
      params: { companyKey: params.companyKey },
    });
  },
});

const companyCardSettingsRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'card-settings',
  component: CompanyCardSettingsScreen,
});

const companyOnlinePaymentsSettingsRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'online-payments-settings',
  component: CompanyOnlinePaymentsSettingsScreen,
});

const companyPrepaidSettingsRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'prepaid-settings',
  component: CompanyPrepaidSettingsScreen,
});

const companySchedulingSettingsRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'scheduling-settings',
  component: CompanySchedulingSettingsScreen,
});

/// O que aparece da empresa na vitrine é assunto à parte do sinal (ADR 0067, decisão 12).
const companyCustomerPortalSettingsRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'customer-portal',
  component: CompanyCustomerPortalSettingsScreen,
});

const companySalesSettingsRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'sales-settings',
  component: CompanySalesSettingsScreen,
});

const companyCommissionRulesRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'commission-rules',
  component: CompanyCommissionRulesScreen,
});

const companyAppearanceRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'appearance',
  component: CompanyAppearanceScreen,
});

const companySecurityRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'security',
  component: CompanySecurityScreen,
});

/// Os endereços de antes da engrenagem continuam valendo: atalho salvo, link mandado a alguém e
/// histórico do navegador levam à mesma página, agora dentro de Configurações, com o recorte que
/// traziam na busca.
function movedToSettings<const Path extends string>(path: Path, target: string) {
  return createRoute({
    getParentRoute: () => companyRoute,
    path,
    beforeLoad: ({ params, search }) => {
      throw redirect({
        to: `/companies/$companyKey/settings/${target}` as '/companies/$companyKey/settings',
        params: { companyKey: (params as { companyKey: string }).companyKey },
        search: search as never,
      });
    },
  });
}

const legacySettingsRoutes = [
  movedToSettings('access', 'access'),
  movedToSettings('profile', 'profile'),
  movedToSettings('units', 'units'),
  movedToSettings('record-templates', 'record-templates'),
  movedToSettings('health-themes', 'health-themes'),
  movedToSettings('audit', 'audit'),
  movedToSettings('communication/templates', 'email-templates'),
  movedToSettings('communication/whatsapp', 'whatsapp-templates'),
];

/// Cada tela repete a conta de permissão que a barra lateral já faz para esconder o item. São as
/// duas pontas da mesma regra: o menu não oferece, e o endereço não abre.
///
/// As oito são declaradas uma a uma, e não montadas num laço, porque é da declaração que sai o
/// tipo de cada endereço — num laço o `Link` perderia a checagem e `/companies/$key/audir` viraria
/// um erro de execução em vez de um erro de compilação.
const companyAccessRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'access',
  validateSearch: accessViewSchema,
  component: function CompanyAccessRouteScreen() {
    const [view, onView] = useViewState(companyAccessRoute.useSearch(), accessViewDefaults);
    return <CompanyAccessScreen view={view} onView={onView} />;
  },
});

const companyProfileRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'profile',
  component: CompanyProfileScreen,
});

const companyUnitsRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'units',
  component: CompanyBusinessUnits,
});

const companyClientsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'clients',
  validateSearch: clientsViewSchema,
  component: function CompanyClientsRouteScreen() {
    const [view, onView] = useViewState(companyClientsRoute.useSearch(), clientsViewDefaults);
    const open = useCompanyLinks();
    const { can, timeZone } = useCompanyScope();
    /// O ícone do Prontuário entra pela composição: Clientes não conhece o módulo que o desenha,
    /// e sem `records.read` ele não aparece — nem o ícone, nem a contagem (ADR 0038, decisão 15).
    return (
      <CompanyClientsScreen
        view={view}
        onView={onView}
        onOpenClient={open.client}
        rowAddon={
          can('records.read')
            ? (client, pageClientIds) => (
                <ClientRecordsButton
                  clientId={client.id}
                  clientName={client.displayName}
                  pageClientIds={pageClientIds}
                  timeZone={timeZone}
                  canManage={can('records.manage')}
                  onOpenChart={(templateId, edit) =>
                    open.clientRecords(client.id, templateId, edit)
                  }
                />
              )
            : undefined
        }
      />
    );
  },
});

/// A página do cliente (ADR 0030): cadastro e atendimentos.
const companyClientRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'clients/$clientId',
  validateSearch: clientPageViewSchema,
  component: function CompanyClientRouteScreen() {
    const { clientId } = companyClientRoute.useParams();
    const [view, onView] = useViewState(companyClientRoute.useSearch(), clientPageViewDefaults);
    const open = useCompanyLinks();
    return (
      <ClientPageScreen
        key={clientId}
        clientId={clientId}
        view={view}
        onView={onView}
        onBack={open.back('clients')}
        onOpenWithdrawal={open.withdrawal}
        onOpenClosing={open.closing}
        onOpenAgenda={open.agendaDay}
        onOpenClient={open.client}
      />
    );
  },
});

const companyClassificationRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'classification',
  validateSearch: classificationViewSchema,
  component: function CompanyClassificationRouteScreen() {
    const [view, onView] = useViewState(
      companyClassificationRoute.useSearch(),
      classificationViewDefaults,
    );
    return <CompanyClassificationScreen view={view} onView={onView} />;
  },
});

const companyClassificationRulesRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'classification-rules',
  validateSearch: classificationRulesViewSchema,
  component: function CompanyClassificationRulesRouteScreen() {
    const [view, onView] = useViewState(
      companyClassificationRulesRoute.useSearch(),
      classificationRulesViewDefaults,
    );
    return <CompanyClassificationRulesScreen view={view} onView={onView} />;
  },
});

const companyChartOfAccountsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'chart-of-accounts',
  component: CompanyChartOfAccountsScreen,
});

const companyDreRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'dre',
  validateSearch: dreViewSchema,
  component: function CompanyDreRouteScreen() {
    const [view, onView] = useViewState(companyDreRoute.useSearch(), dreViewDefaults);
    return <CompanyDreScreen view={view} onView={onView} />;
  },
});

const companyCashFlowRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'cash-flow',
  validateSearch: cashFlowViewSchema,
  component: function CompanyCashFlowRouteScreen() {
    const [view, onView] = useViewState(companyCashFlowRoute.useSearch(), cashFlowViewDefaults);
    return <CompanyCashFlowScreen view={view} onView={onView} />;
  },
});

const companyAccountingClosingRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'accounting-closing',
  component: CompanyAccountingClosingScreen,
});

const companyPrepaidWithdrawalsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'prepaid-withdrawals',
  validateSearch: z.object({ balance: z.uuid().optional().catch(undefined) }),
  component: function CompanyPrepaidWithdrawalsRouteScreen() {
    const search = companyPrepaidWithdrawalsRoute.useSearch();
    const navigate = useNavigate();
    return (
      <CompanyPrepaidWithdrawalsScreen
        balanceId={search.balance}
        onClearBalance={() => void navigate({ to: '.', search: {} })}
      />
    );
  },
});

const companyServicesRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'services',
  validateSearch: servicesViewSchema,
  component: function CompanyServicesRouteScreen() {
    const [view, onView] = useViewState(companyServicesRoute.useSearch(), servicesViewDefaults);
    return <CompanyServicesScreen view={view} onView={onView} />;
  },
});

/// Vendas abre no histórico; a Nova venda é a ação que ele oferece (ADR 0055).
const companyCommissionsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'commissions',
  component: function CompanyCommissionsRouteScreen() {
    const open = useCompanyLinks();
    return <CompanyCommissionsScreen onOpenExpenses={open.commissionExpenses} />;
  },
});

const companySalesRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'sales',
  validateSearch: salesViewSchema,
  component: function CompanySalesRouteScreen() {
    const [view, onView] = useViewState(companySalesRoute.useSearch(), salesViewDefaults);
    const open = useCompanyLinks();
    return (
      <CompanySalesScreen
        page="history"
        view={view}
        onView={onView}
        onNewSale={open.newSale}
        onBack={open.newSale}
        onSchedule={open.bookSoldService}
      />
    );
  },
});

const companyNewSaleRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'sales/new',
  validateSearch: salesViewSchema,
  component: function CompanyNewSaleRouteScreen() {
    const [view, onView] = useViewState(companyNewSaleRoute.useSearch(), newSaleViewDefaults);
    const open = useCompanyLinks();
    return (
      <CompanySalesScreen
        page="new"
        view={view}
        onView={onView}
        onNewSale={open.newSale}
        onBack={view.appointment ? () => open.closing(view.appointment!) : open.back('sales')}
        onOpenAppointment={open.closing}
      />
    );
  },
});

/// Pacotes e Promoções moram no Catálogo, ao lado de Serviços e Produtos (ADR 0055).
const companyPackagesRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'packages',
  validateSearch: salesTermsViewSchema,
  component: function CompanyPackagesRouteScreen() {
    const [view, onView] = useViewState(companyPackagesRoute.useSearch(), salesTermsViewDefaults);
    const open = useCompanyLinks();
    return <CompanyPackagesScreen view={view} onView={onView} onOpenPackage={open.salesPackage} />;
  },
});

/// A página do pacote (ADR 0054); `new` cadastra um pacote novo.
const companySalesPackageRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'packages/$packageId',
  component: function CompanySalesPackageRouteScreen() {
    const { packageId } = companySalesPackageRoute.useParams();
    const open = useCompanyLinks();
    return (
      <CompanySalesPackageScreen
        key={packageId}
        packageId={packageId}
        onBack={open.back('packages')}
      />
    );
  },
});

const companyPromotionsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'promotions',
  validateSearch: salesTermsViewSchema,
  component: function CompanyPromotionsRouteScreen() {
    const [view, onView] = useViewState(companyPromotionsRoute.useSearch(), salesTermsViewDefaults);
    return <CompanyPromotionsScreen view={view} onView={onView} />;
  },
});

/// Produtos no Catálogo; o cadastro continua do Estoque (ADR 0055).
const companyProductsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'products',
  validateSearch: productsViewSchema,
  component: function CompanyProductsRouteScreen() {
    const [view, onView] = useViewState(companyProductsRoute.useSearch(), productsViewDefaults);
    return <CompanyProductsScreen view={view} onView={onView} />;
  },
});

const companyInventoryRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'inventory',
  validateSearch: inventoryViewSchema,
  component: function CompanyInventoryRouteScreen() {
    const [view, onView] = useViewState(companyInventoryRoute.useSearch(), inventoryViewDefaults);
    return <CompanyInventoryScreen view={view} onView={onView} />;
  },
});

const companyPurchasingRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'purchasing',
  validateSearch: purchasingViewSchema,
  component: function CompanyPurchasingRouteScreen() {
    const [view, onView] = useViewState(companyPurchasingRoute.useSearch(), purchasingViewDefaults);
    return <CompanyPurchasingScreen view={view} onView={onView} />;
  },
});

const companyProtocolsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'protocols',
  validateSearch: protocolsViewSchema,
  component: function CompanyProtocolsRouteScreen() {
    const [view, onView] = useViewState(companyProtocolsRoute.useSearch(), protocolsViewDefaults);
    return <CompanyProtocolsScreen view={view} onView={onView} />;
  },
});

const companyRecordTemplatesRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'record-templates',
  validateSearch: recordTemplatesViewSchema,
  component: function CompanyRecordTemplatesRouteScreen() {
    const [view, onView] = useViewState(
      companyRecordTemplatesRoute.useSearch(),
      recordTemplatesViewDefaults,
    );
    const open = useCompanyLinks();
    return (
      <CompanyRecordTemplatesScreen view={view} onView={onView} onOpenThemes={open.healthThemes} />
    );
  },
});

const companyHealthThemesRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'health-themes',
  component: CompanyHealthThemesScreen,
});

const companyProtocolApplicationsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'protocol-applications',
  validateSearch: protocolApplicationsViewSchema,
  component: function CompanyProtocolApplicationsRouteScreen() {
    const [view, onView] = useViewState(
      companyProtocolApplicationsRoute.useSearch(),
      protocolApplicationsViewDefaults,
    );
    return <CompanyProtocolApplicationsScreen view={view} onView={onView} />;
  },
});

const companyMaterialUsageRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'material-usage',
  validateSearch: materialUsageViewSchema,
  component: function CompanyMaterialUsageRouteScreen() {
    const [view, onView] = useViewState(
      companyMaterialUsageRoute.useSearch(),
      materialUsageViewDefaults,
    );
    const open = useCompanyLinks();
    return <CompanyMaterialUsageScreen view={view} onView={onView} onOpenClosing={open.closing} />;
  },
});

const companyMaterialSufficiencyRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'material-sufficiency',
  validateSearch: materialSufficiencyViewSchema,
  component: function CompanyMaterialSufficiencyRouteScreen() {
    const [view, onView] = useViewState(
      companyMaterialSufficiencyRoute.useSearch(),
      materialSufficiencyViewDefaults,
    );
    return <CompanyMaterialSufficiencyScreen view={view} onView={onView} />;
  },
});

const companySchedulingRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'scheduling',
  validateSearch: schedulingViewSchema,
  component: function CompanySchedulingRouteScreen() {
    const [view, onView] = useViewState(companySchedulingRoute.useSearch(), schedulingViewDefaults);
    const open = useCompanyLinks();
    return (
      <CompanySchedulingScreen
        view={view}
        onView={onView}
        onOpenClosing={open.closing}
        onOpenClient={open.client}
        onOpenClientBalances={open.clientBalances}
        onOpenProfessionals={open.professionals}
      />
    );
  },
});

/// Profissionais: dado da Agenda, em tela própria (ADR 0083).
const companyProfessionalsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'professionals',
  validateSearch: professionalsViewSchema,
  component: function CompanyProfessionalsRouteScreen() {
    const [view, onView] = useViewState(
      companyProfessionalsRoute.useSearch(),
      professionalsViewDefaults,
    );
    return <CompanyProfessionalsScreen view={view} onView={onView} />;
  },
});

/// O fechamento do atendimento (ADR 0030): uma página só, montada pela composição.
const companyAppointmentClosingRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'scheduling/appointments/$appointmentId/closing',
  component: function CompanyAppointmentClosingRouteScreen() {
    const { appointmentId } = companyAppointmentClosingRoute.useParams();
    const open = useCompanyLinks();
    return (
      <AppointmentClosingScreen
        key={appointmentId}
        appointmentId={appointmentId}
        onBack={open.back('scheduling')}
        onOpenClient={open.client}
        onOpenSale={open.appointmentSale}
      />
    );
  },
});

/// Os atalhos entre telas da empresa que só a composição sabe endereçar: a agenda não conhece o
/// endereço do cliente, e o cliente não conhece o da agenda.
function useCompanyLinks() {
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const router = useRouter();
  const { companyKey } = companyRoute.useParams();
  return {
    withdrawal: (balanceId: string) =>
      void navigate({
        to: '/companies/$companyKey/prepaid-withdrawals',
        params: { companyKey },
        search: { balance: balanceId },
      }),
    clientBalances: (clientId: string) =>
      void navigate({
        to: '/companies/$companyKey/clients/$clientId',
        params: { companyKey, clientId },
        search: { tab: 'balances' },
      }),
    client: (clientId: string) =>
      void navigate({
        to: '/companies/$companyKey/clients/$clientId',
        params: { companyKey, clientId },
      }),
    /// A aba Prontuário do cliente, com a ficha aberta quando o modal da lista escolheu uma.
    clientRecords: (clientId: string, templateId?: string, edit?: boolean) =>
      void navigate({
        to: '/companies/$companyKey/clients/$clientId',
        params: { companyKey, clientId },
        search: templateId
          ? { tab: 'records', section: 'records', record: templateId, ...(edit ? { edit } : {}) }
          : { tab: 'records' },
      }),
    healthThemes: () =>
      void navigate({
        to: '/companies/$companyKey/settings/health-themes',
        params: { companyKey },
      }),
    closing: (appointmentId: string) =>
      void navigate({
        to: '/companies/$companyKey/scheduling/appointments/$appointmentId/closing',
        params: { companyKey, appointmentId },
      }),
    salesPackage: (packageId: string) =>
      void navigate({
        to: '/companies/$companyKey/packages/$packageId',
        params: { companyKey, packageId },
      }),
    newSale: () =>
      void navigate({ to: '/companies/$companyKey/sales/new', params: { companyKey } }),
    /// A Nova venda do atendimento, com o cliente e o serviço dele (ADR 0059).
    appointmentSale: (appointmentId: string) =>
      void navigate({
        to: '/companies/$companyKey/sales/new',
        params: { companyKey },
        search: { appointment: appointmentId },
      }),
    /// Abre a Agenda com o Novo agendamento do serviço já vendido (ADR 0046).
    bookSoldService: (saleItemUnitId: string) =>
      void navigate({
        to: '/companies/$companyKey/scheduling',
        params: { companyKey },
        search: { tab: 'calendar', book: saleItemUnitId },
      }),
    /// As despesas de comissão de quem recebe, inclusive as já pagas (ADR 0082, decisão 30).
    commissionExpenses: (professional: string) =>
      void navigate({
        to: '/companies/$companyKey/payables',
        params: { companyKey },
        search: {
          origin: ['sales-commissions'],
          counterparty: [professional],
          status: ['UPCOMING', 'OVERDUE', 'PAID'],
        },
      }),
    professionals: () =>
      void navigate({ to: '/companies/$companyKey/professionals', params: { companyKey } }),
    agendaDay: (date: string) =>
      void navigate({
        to: '/companies/$companyKey/scheduling',
        params: { companyKey },
        search: { tab: 'calendar', view: 'day', date },
      }),
    /// Voltar leva à posição de onde a pessoa veio; aberto direto pelo endereço, cai na lista.
    back: (fallback: 'clients' | 'scheduling' | 'packages' | 'sales') => () => {
      if (canGoBack) router.history.back();
      else if (fallback === 'packages')
        void navigate({ to: '/companies/$companyKey/packages', params: { companyKey } });
      else if (fallback === 'sales')
        void navigate({ to: '/companies/$companyKey/sales', params: { companyKey } });
      else
        void navigate({
          to:
            fallback === 'clients'
              ? '/companies/$companyKey/clients'
              : '/companies/$companyKey/scheduling',
          params: { companyKey },
        });
    },
  };
}

const companyPaymentReportsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'payment-reports',
  component: CompanyPaymentReportsScreen,
});

const companyFinanceRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'finance',
  validateSearch: financeViewSchema,
  component: function CompanyFinanceRouteScreen() {
    const [view, onView] = useViewState(companyFinanceRoute.useSearch(), financeViewDefaults);
    return <CompanyFinanceScreen view={view} onView={onView} />;
  },
});

const companyPayablesRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'payables',
  validateSearch: payablesViewSchema,
  component: function CompanyPayablesRouteScreen() {
    const [view, onView] = useViewState(companyPayablesRoute.useSearch(), payablesViewDefaults);
    return <CompanyPayablesScreen direction="PAYABLE" view={view} onView={onView} />;
  },
});

const companyReceivablesRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'receivables',
  validateSearch: payablesViewSchema,
  component: function CompanyReceivablesRouteScreen() {
    const [view, onView] = useViewState(companyReceivablesRoute.useSearch(), payablesViewDefaults);
    return <CompanyPayablesScreen direction="RECEIVABLE" view={view} onView={onView} />;
  },
});

/// A vitrine é a porta de entrada do sistema (ADR 0067): a raiz é dela, com ou sem login.
const publicPortalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: PublicPortal,
});
const publicPortalCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/c/$companyKey',
  component: function PublicCompanyScreen() {
    const { companyKey } = publicPortalCompanyRoute.useParams();
    return <PublicPortal companyKey={companyKey} />;
  },
});
/// Endereços da primeira versão do portal: quem guardou o link chega ao lugar novo.
const legacyBookRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/book',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
});
const legacyBookCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/book/$companyKey',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/c/$companyKey', params: { companyKey: params.companyKey } });
  },
});
const legacyCustomerPortalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/$companyKey',
  beforeLoad: () => {
    throw redirect({ to: '/account' });
  },
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  validateSearch: nextSearch,
  component: function SignupScreen() {
    const { next } = signupRoute.useSearch();
    return <AccountSignupScreen next={next} />;
  },
});
const confirmAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirm-account',
  validateSearch: tokenSearch.extend(nextSearch.shape),
  beforeLoad: ({ search }) => {
    if (!search.token) throw redirect({ to: '/signup' });
    return { token: search.token };
  },
  component: function ConfirmAccountScreen() {
    const { token } = confirmAccountRoute.useRouteContext();
    const { next } = confirmAccountRoute.useSearch();
    return <AccountConfirmationScreen token={token} next={next} />;
  },
});
const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirm-email',
  validateSearch: tokenSearch,
  beforeLoad: ({ search }) => {
    if (!search.token) throw redirect({ to: '/' });
    return { token: search.token };
  },
  component: function ConfirmEmailScreen() {
    const { token } = confirmEmailRoute.useRouteContext();
    return <EmailConfirmationScreen token={token} />;
  },
});

const accountTabs = [
  'appointments',
  'profile',
  'privacy',
] as const satisfies readonly CustomerAccountTab[];
/// A área do cliente vale em qualquer contexto pessoal: a conta é da pessoa (ADR 0067).
const customerAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  validateSearch: z.object({ tab: z.enum(accountTabs).optional().catch(undefined) }),
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context.queryClient);
    if (!session) throw redirect({ to: '/login', search: { next: '/account' } });
    if (session.context?.kind === 'PLATFORM_ACTING_ON_TENANT') throw redirect({ to: '/home' });
  },
  component: function AccountScreen() {
    const { tab } = customerAccountRoute.useSearch();
    const navigate = useNavigate();
    return (
      <CustomerAccountScreen
        tab={tab ?? 'appointments'}
        onTab={(next) =>
          void navigate({
            to: '/account',
            search: { tab: next === 'appointments' ? undefined : next },
            replace: true,
          })
        }
      />
    );
  },
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: function PrivacyScreen() {
    return <LegalDocumentScreen document="PRIVACY" />;
  },
});
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: function TermsScreen() {
    return <LegalDocumentScreen document="TERMS" />;
  },
});

const companyPartnersRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'partners',
  validateSearch: partnersViewSchema,
  component: function CompanyPartnersRouteScreen() {
    const [view, onView] = useViewState(companyPartnersRoute.useSearch(), partnersViewDefaults);
    return <CompanyPartnersScreen view={view} onView={onView} />;
  },
});

const companyContributionsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'contributions',
  validateSearch: contributionsViewSchema,
  component: function CompanyContributionsRouteScreen() {
    const [view, onView] = useViewState(
      companyContributionsRoute.useSearch(),
      contributionsViewDefaults,
    );
    return <CompanyContributionsScreen view={view} onView={onView} />;
  },
});

const companyEntriesRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'entries',
  validateSearch: entriesViewSchema,
  component: function CompanyEntriesRouteScreen() {
    const [view, onView] = useViewState(companyEntriesRoute.useSearch(), entriesViewDefaults);
    return <CompanyEntriesScreen view={view} onView={onView} />;
  },
});

const companyMetricsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'metrics',
  component: CompanyMetricsScreen,
});

/// O recorte da lista — filtro, ordenação e página — é lido aqui e entregue à tela. É a rota que
/// sabe ler a URL, e é por isso que a tela não a lê sozinha: a mesma tela pode pender de mais de
/// uma rota.
const companyAuditRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'audit',
  validateSearch: auditViewSchema,
  component: function CompanyAuditRouteScreen() {
    const [view, onView] = useViewState(companyAuditRoute.useSearch(), auditViewDefaults);
    return <CompanyAuditScreen view={view} onView={onView} />;
  },
});

/// Comunicação da empresa: o mesmo desenho do console, um endereço com abas dentro.
///
/// Antes eram dois itens soltos na barra lateral, "Caixa de saída" e "Modelos de e-mail" — a mesma
/// dupla que do lado da plataforma já vivia junta. Duas formas para a mesma coisa fazem quem
/// aprende um dos lados reaprender o outro.
const companyCommunicationRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: 'communication',
});

/// O endereço nu não é tela nenhuma: cai na primeira aba que esta pessoa alcança.
///
/// "A primeira" não é sempre a caixa de saída. Quem tem só `email_templates.read` clicaria em
/// Comunicação para chegar num endereço inexistente — o índice mandando para uma tela que o portão
/// dela recusa. Por isso a conta de permissão é feita aqui também, antes de escolher o destino.
const companyCommunicationIndexRoute = createRoute({
  getParentRoute: () => companyCommunicationRoute,
  path: '/',
  beforeLoad: async ({ context, params }) => {
    const session = await loadSession(context.queryClient);
    const company = session?.currentTenant;
    const reaches = (permission: string) =>
      session?.platformRole === 'ROOT' || (company?.permissions ?? []).includes(permission);
    /// A caixa de saída tem recorte na URL, e por isso o destino precisa dizer com qual recorte
    /// chega. Vazio é o recém-aberto — que é o certo para quem só clicou em "Comunicação".
    if (reaches('email_messages.read')) {
      throw redirect({
        to: '/companies/$companyKey/communication/outbox',
        params: { companyKey: params.companyKey },
        search: {},
      });
    }
    /// Os modelos saíram daqui para Configurações (ADR 0041); quem só alcança eles e ainda chega
    /// pelo endereço antigo cai no lugar novo, e não num endereço inexistente.
    if (reaches('email_templates.read')) {
      throw redirect({
        to: '/companies/$companyKey/settings/email-templates',
        params: { companyKey: params.companyKey },
      });
    }
    throw redirect({
      to: '/companies/$companyKey/settings/whatsapp-templates',
      params: { companyKey: params.companyKey },
    });
  },
});

const companyOutboxRoute = createRoute({
  getParentRoute: () => companyCommunicationRoute,
  path: 'outbox',
  validateSearch: outboxViewSchema,
  component: function CompanyOutboxRouteScreen() {
    const [view, onView] = useViewState(companyOutboxRoute.useSearch(), outboxViewDefaults);
    return <CompanyOutboxScreen view={view} onView={onView} />;
  },
});

const companyTemplatesRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'email-templates',
  validateSearch: templatesViewSchema,
  component: function CompanyTemplatesRouteScreen() {
    const [view, onView] = useViewState(companyTemplatesRoute.useSearch(), templatesViewDefaults);
    return <CompanyTemplatesScreen view={view} onView={onView} />;
  },
});

/// As mensagens de WhatsApp da empresa (ADR 0036).
const companyWhatsAppRoute = createRoute({
  getParentRoute: () => companySettingsRoute,
  path: 'whatsapp-templates',
  component: CompanyWhatsAppScreen,
});

/// Todo endereço que não é nenhum dos de cima.
///
/// Para quem está logado, o 404 aparece **dentro da casca**: o menu continua ao lado, porque quem
/// se perdeu precisa de para onde ir — e a casca é a de onde a pessoa está, empresa ou plataforma.
/// Para quem não está logado não existe casca nenhuma para mostrar, e um 404 anônimo só ensinaria
/// que aquele endereço não é de nada, o que é conversa para depois de entrar. Essa pessoa vai para
/// a entrada.
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$',
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context.queryClient);
    if (!session) throw redirect({ to: '/login' });
    // Quem está como cliente não tem casca administrativa: o endereço solto volta à vitrine.
    if (session.context?.kind === 'CUSTOMER') throw redirect({ to: '/' });
    return { inTenant: Boolean(session.currentTenant) };
  },
  component: function NotFoundScreen() {
    const { inTenant } = notFoundRoute.useRouteContext();
    return inTenant ? (
      <CompanyLayout content={<RouteNotFound />} />
    ) : (
      <ConsoleLayout content={<RouteNotFound />} />
    );
  },
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  activateOwnerRoute,
  activateMemberRoute,
  activateClientRoute,
  publicPortalRoute,
  publicPortalCompanyRoute,
  legacyBookRoute,
  legacyBookCompanyRoute,
  legacyCustomerPortalRoute,
  signupRoute,
  confirmAccountRoute,
  confirmEmailRoute,
  customerAccountRoute,
  privacyRoute,
  termsRoute,
  consoleRoute.addChildren([
    platformAccessRoute,
    companiesRoute,
    plansRoute,
    technicalDocumentationRoute,
    communicationRoute.addChildren([
      communicationIndexRoute,
      outboxRoute,
      templatesRoute,
      communicationSettingsRoute,
    ]),
  ]),
  companyRoute.addChildren([
    companyHomeRoute,
    companyDocumentationRoute,
    companySettingsRoute.addChildren([
      companySettingsIndexRoute,
      companyAppearanceRoute,
      companySecurityRoute,
      companyCardSettingsRoute,
      companySalesSettingsRoute,
      companyCommissionRulesRoute,
      companySchedulingSettingsRoute,
      companyCustomerPortalSettingsRoute,
      companyPrepaidSettingsRoute,
      companyOnlinePaymentsSettingsRoute,
      companyProfileRoute,
      companyUnitsRoute,
      companyAccessRoute,
      companyRecordTemplatesRoute,
      companyHealthThemesRoute,
      companyTemplatesRoute,
      companyWhatsAppRoute,
      companyAuditRoute,
    ]),
    ...legacySettingsRoutes,
    companyClientsRoute,
    companyClientRoute,
    companyPrepaidWithdrawalsRoute,
    companyChartOfAccountsRoute,
    companyDreRoute,
    companyCashFlowRoute,
    companyAccountingClosingRoute,
    companyClassificationRoute,
    companyClassificationRulesRoute,
    companyServicesRoute,
    companySalesRoute,
    companyCommissionsRoute,
    companyNewSaleRoute,
    companyPackagesRoute,
    companySalesPackageRoute,
    companyPromotionsRoute,
    companyProductsRoute,
    companyInventoryRoute,
    companyPurchasingRoute,
    companyProtocolsRoute,
    companyProtocolApplicationsRoute,
    companyMaterialUsageRoute,
    companyMaterialSufficiencyRoute,
    companySchedulingRoute,
    companyProfessionalsRoute,
    companyAppointmentClosingRoute,
    companyFinanceRoute,
    companyPaymentReportsRoute,
    companyPayablesRoute,
    companyReceivablesRoute,
    companyPartnersRoute,
    companyContributionsRoute,
    companyEntriesRoute,
    companyMetricsRoute,
    companyCommunicationRoute.addChildren([companyCommunicationIndexRoute, companyOutboxRoute]),
  ]),
  notFoundRoute,
]);

/// O roteador nasce por árvore montada, e não uma vez por módulo.
///
/// Ele guarda o histórico e a sessão resolvida; um só, criado na importação, seria compartilhado
/// entre os testes e faria o endereço de um vazar para o outro. Custa uma instância por `App`, que
/// em produção é uma.
export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    stringifySearch: stringifyListSearch,
    context: { queryClient },
    defaultPendingComponent: PendingSession,
    /// Sem espera para começar a mostrar e sem tempo mínimo depois: a tela de carregando é a mesma
    /// que existia antes da rota, e ela aparecia na hora.
    defaultPendingMs: 0,
    defaultPendingMinMs: 0,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
